import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MaxsoldWatchlistIdsDto } from '@jordan/types';
import { apiDelete, apiGet, apiPost } from '../../lib/api';

export interface WatchlistApi {
  /** True when Maxsold account credentials are configured (favouriting is possible). */
  configured: boolean;
  isFavourite: (id: number) => boolean;
  isPending: (id: number) => boolean;
  /** Optimistically toggle a lot's favourite state (reverts on failure). */
  toggle: (id: number) => void;
}

/**
 * Loads the current watchlist ids once and exposes an optimistic favourite/un-favourite toggle.
 * The returned object is stable across renders except when the watchlist state actually changes,
 * so it can be a `useMemo` dependency for table columns.
 */
export function useWatchlist(): WatchlistApi {
  const [configured, setConfigured] = useState(false);
  const [ids, setIds] = useState<Set<number>>(new Set());
  const [pending, setPending] = useState<Set<number>>(new Set());
  const idsRef = useRef(ids);
  idsRef.current = ids;

  useEffect(() => {
    let cancelled = false;
    apiGet<MaxsoldWatchlistIdsDto>('/api/maxsold/watchlist/ids')
      .then((r) => {
        if (cancelled) return;
        setConfigured(r.configured);
        setIds(new Set(r.ids));
      })
      .catch(() => {
        /* leave hearts hidden if we can't load the watchlist */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const toggle = useCallback((id: number) => {
    const wasFav = idsRef.current.has(id);
    setIds((prev) => {
      const next = new Set(prev);
      if (wasFav) next.delete(id);
      else next.add(id);
      return next;
    });
    setPending((prev) => new Set(prev).add(id));

    const request = wasFav ? apiDelete(`/api/maxsold/watchlist/${id}`) : apiPost(`/api/maxsold/watchlist/${id}`);
    request
      .catch(() => {
        // Revert the optimistic change if the server rejected it.
        setIds((prev) => {
          const next = new Set(prev);
          if (wasFav) next.add(id);
          else next.delete(id);
          return next;
        });
      })
      .finally(() =>
        setPending((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        }),
      );
  }, []);

  return useMemo(
    () => ({
      configured,
      isFavourite: (id: number) => ids.has(id),
      isPending: (id: number) => pending.has(id),
      toggle,
    }),
    [configured, ids, pending, toggle],
  );
}

interface HeartProps {
  favourite: boolean;
  pending: boolean;
  onToggle: () => void;
  /** Heart glyph size in px (the button is a little larger). */
  size?: number;
}

/**
 * A favourite toggle: the Phosphor heart shape as an inline SVG (this template's Phosphor font is
 * outline-only, so an SVG is used to render a genuinely filled red heart when favourited). A subtle
 * circular backdrop keeps it legible over photos. Clicks don't bubble to a clickable card/row.
 */
export function FavouriteHeart({ favourite, pending, onToggle, size = 22 }: HeartProps) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        if (!pending) onToggle();
      }}
      onKeyDown={(e) => e.stopPropagation()}
      disabled={pending}
      aria-pressed={favourite}
      title={favourite ? 'Remove from favourites' : 'Add to favourites'}
      className="d-inline-flex align-items-center justify-content-center border-0 p-0"
      style={{
        width: size + 12,
        height: size + 12,
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.85)',
        boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
        cursor: pending ? 'default' : 'pointer',
        opacity: pending ? 0.6 : 1,
        lineHeight: 0,
      }}
    >
      <svg viewBox="0 0 256 256" width={size} height={size} aria-hidden="true">
        <path
          d="M178,32c-20.65,0-38.73,8.88-50,23.89C116.73,40.88,98.65,32,78,32A62.07,62.07,0,0,0,16,94c0,70,103.79,126.66,108.21,129a8,8,0,0,0,7.58,0C136.21,220.66,240,164,240,94A62.07,62.07,0,0,0,178,32Z"
          fill={favourite ? '#dc3545' : 'none'}
          stroke={favourite ? '#dc3545' : '#5a6a85'}
          strokeWidth={favourite ? 0 : 18}
        />
      </svg>
    </button>
  );
}
